import React, { useState, MouseEvent, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { Download, Calendar, RefreshCw, ClipboardCheck } from 'lucide-react';

import { AppConfig } from '../../../../config/app.config';

import { EWhiteLabelingClient } from '../../../../types/misc.enum';
import { IMBTSFeedbackResponse } from '../../../../types/entities.dto';

import { useSaveFeedback } from '../../../../hooks/mbts-ux';

import { ConsentLock } from '../ConsentLock';

import { getWhiteLabelingClient } from '../../../../helpers/misc.helper';

import { SECTION_IDS } from './utils';

interface FeedbackProps {
    scenarioId: string | null;
    analysisId: number;
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};

export const Feedback = (props: FeedbackProps) => {
    const { scenarioId, analysisId, isConsentLocked, openConsentDataGather } = props;

    const navigate = useNavigate();

    const questions: { key: string; title: string; legendStart: string; legendEnd: string }[] = [
        {
            key: 'insight_clarity',
            title: 'How clear and actionable were the insights provided in this report?',
            legendStart: 'Not Clear',
            legendEnd: 'Very Clear',
        },
        {
            key: 'visualization_helpfulness',
            title: 'How well did the visual data representations help you understand your performance?',
            legendStart: 'Not Helpful',
            legendEnd: 'Very Helpful',
        },
        {
            key: 'recommendation_likelihood',
            title: 'How likely are you to recommend this assessment to colleagues?',
            legendStart: 'Not Likely',
            legendEnd: 'Very Likely',
        },
    ];

    const [responses, setResponses] = useState<IMBTSFeedbackResponse[]>([]);
    const [improvement, setImprovement] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const { mutate: save } = useSaveFeedback();

    const isSubmitDisabled = () => isProcessing || isSuccess || responses.length !== questions.length;

    const getRating = (questionTitle: string): number | null => {
        const found = responses.find(r => r.question === questionTitle);
        return found ? found.rating : null;
    };

    const setRating = (questionTitle: string, rating: number) => {
        setResponses(prev => {
            const qIdx = prev.findIndex(r => r.question === questionTitle);
            const newList = [...prev];
            if (qIdx !== -1) {
                newList[qIdx] = { ...newList[qIdx], rating };
            } else {
                newList.push({ question: questionTitle, rating });
            }
            return newList;
        });
    };

    const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (isSubmitDisabled()) return;

        save({
            payload: {
                analysis_id: analysisId,
                responses,
                improvement: improvement.length ? improvement : null,
            },
            setIsProcessing,
            setIsSuccess,
        });
    };

    const handleOpenCalender = () => {
        if (getWhiteLabelingClient() === EWhiteLabelingClient.RIDLEY_CONSULTING || AppConfig.app.isDemo) {
            return window.open('https://calendly.com/bethridley1/30-minute-meeting-clone-1', '_blank');
        }
        window.open('https://calendly.com/mindshield/30min', '_blank');
    };

    const isRidley = getWhiteLabelingClient() === EWhiteLabelingClient.RIDLEY_CONSULTING;

    return (
        <section id={SECTION_IDS.feedback} className="mbts_analysis_v2__section mbts_analysis_v2__feedback">
            <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />

            <div className="mbts_analysis_v2__feedback__grid">
                <div className="mbts_analysis_v2__feedback__questions">
                    <div className="mbts_analysis_v2__feedback__intro">
                        <h3 className="mbts_analysis_v2__feedback__title">How helpful was this cognitive debrief?</h3>
                        <p className="mbts_analysis_v2__feedback__subtitle">
                            Your feedback shapes our MindShield leadership simulation algorithms.
                        </p>
                    </div>

                    {!isSuccess && (
                        <Fragment>
                            <div className="mbts_analysis_v2__feedback__questions__list">
                                {questions.map((que, idx) => {
                                    const selected = getRating(que.title);

                                    return (
                                        <div key={que.key} className="mbts_analysis_v2__feedback__question">
                                            <p className="mbts_analysis_v2__feedback__question__label">
                                                {idx + 1}. {que.title}
                                            </p>
                                            <div className="mbts_analysis_v2__feedback__question__scale">
                                                <span className="mbts_analysis_v2__feedback__question__legend">{que.legendStart}</span>
                                                <div className="mbts_analysis_v2__feedback__question__ratings">
                                                    {Array.from({ length: 5 }).map((_, ratingIdx) => {
                                                        const value = ratingIdx + 1;
                                                        const isActive = selected === value;

                                                        return (
                                                            <button
                                                                key={`${que.key}_${value}`}
                                                                type="button"
                                                                className={`mbts_analysis_v2__feedback__rating_btn${isActive ? ' mbts_analysis_v2__feedback__rating_btn--active' : ''}`}
                                                                onClick={() => setRating(que.title, value)}
                                                                aria-pressed={isActive}
                                                                aria-label={`Rate ${value} for ${que.title}`}
                                                            >
                                                                {value}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <span className="mbts_analysis_v2__feedback__question__legend">{que.legendEnd}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mbts_analysis_v2__feedback__improvement">
                                <textarea
                                    rows={2}
                                    placeholder="Share anything that felt unclear or could be improved..."
                                    value={improvement}
                                    onChange={e => setImprovement(e.target.value)}
                                />
                            </div>

                            <button
                                type="button"
                                className="mbts_analysis_v2__feedback__submit"
                                disabled={isSubmitDisabled()}
                                onClick={onSubmit}
                            >
                                {isProcessing && <span className="btn-loader" />}
                                {isProcessing ? <span>Submitting..</span> : <span>Submit Feedback</span>}
                            </button>
                        </Fragment>
                    )}

                    {isSuccess && (
                        <div className="mbts_analysis_v2__feedback__success">
                            <ClipboardCheck />
                            <p className="mbts_analysis_v2__feedback__success__title">Thanks for your feedback!</p>
                            <p className="mbts_analysis_v2__feedback__success__body">Your feedback has been recorded successfully.</p>
                        </div>
                    )}
                </div>

                <div className="mbts_analysis_v2__feedback__actions">
                    <button
                        type="button"
                        className="mbts_analysis_v2__feedback__action mbts_analysis_v2__feedback__action--download"
                        onClick={() => window.print()}
                    >
                        <div className="mbts_analysis_v2__feedback__action__icon">
                            <Download />
                        </div>
                        <div className="mbts_analysis_v2__feedback__action__copy">
                            <p className="mbts_analysis_v2__feedback__action__title">Download PDF Summary</p>
                            <p className="mbts_analysis_v2__feedback__action__desc">Save a clean PDF copy for your leadership portfolio</p>
                        </div>
                    </button>

                    <button
                        type="button"
                        className="mbts_analysis_v2__feedback__action mbts_analysis_v2__feedback__action--retake"
                        onClick={() => (isRidley ? null : navigate(`/mbts/${scenarioId}/personas`, { replace: true }))}
                        disabled={isRidley}
                    >
                        <div className="mbts_analysis_v2__feedback__action__icon">
                            <RefreshCw />
                        </div>
                        <div className="mbts_analysis_v2__feedback__action__copy">
                            <p className="mbts_analysis_v2__feedback__action__title">Retake Assessment</p>
                            <p className="mbts_analysis_v2__feedback__action__desc">Practice again to track immediate improvements</p>
                        </div>
                    </button>

                    <button
                        type="button"
                        className="mbts_analysis_v2__feedback__action mbts_analysis_v2__feedback__action--book"
                        onClick={handleOpenCalender}
                    >
                        <div className="mbts_analysis_v2__feedback__action__icon">
                            <Calendar />
                        </div>
                        <div className="mbts_analysis_v2__feedback__action__copy">
                            <p className="mbts_analysis_v2__feedback__action__title">Book Executive Debrief</p>
                            <p className="mbts_analysis_v2__feedback__action__desc">1-on-1 session with a MindShield lead coach</p>
                        </div>
                    </button>
                </div>
            </div>
        </section>
    );
};
